import { Component } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { isChunkLoadError, reloadForFreshChunks } from '../utils/lazyWithRetry';

// Catches errors thrown while rendering a route (including a lazy import that
// rejects because its hashed chunk 404'd after a redeploy). Without this, such
// an error unmounts the whole route tree and the page renders blank — the
// idle-tab-then-navigate bug.
class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error) {
    // A stale chunk is recoverable: reload once to fetch the current build.
    if (isChunkLoadError(error)) {
      reloadForFreshChunks();
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Chunk errors trigger a reload in componentDidCatch; render nothing
      // (not a broken UI) while the reload takes over.
      if (this.state.isChunkError) return null;

      return (
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            Please reload the page. If the problem continues, contact support.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
